import { AxiosResponse } from 'axios';
import {
  GenerateDailyWorkReceiptParams,
  ReceiptUserListItem,
  SerDailyWorkBankHourData,
} from '../@types/daily-work';
import {
  DailyWork,
  DailyWorkResponse,
  DailyWorkType,
} from '../models/daily-work';
import { File } from '../models/file';
import { UserRole } from '../models/user';
import { api } from '../providers/base-api';
import { PaginatedResponse } from '../utils/types/data/pagination';
import {
  AddDailyWorkJustificationRequetData,
  AddDailyWorkManuallyRequetData,
  AddDailyWorkPartialJustificationRequetData,
  EditAppointmentsRequestData,
  EditDailyWorkData,
  EditDailyWorkJustificationRequetData,
  FaultToJustificationRequestData,
  GenerateUserMirrorRequestParams,
  GetLateWorkRequestQuery,
  GetLateWorkResponse,
  GetLunchAlertsResponse,
} from '../utils/types/data/services/daily-work';

export async function createDailyWorkRequest(data: {
  location?: string;
}): Promise<DailyWork> {
  const response = await api.post<DailyWork>('/work-histories', data);

  return response.data;
}

export async function updateDailyWorkRequest(
  id: number,
  data: { type: DailyWorkType; location?: string },
): Promise<DailyWork> {
  const response = await api.put<DailyWork>(`/work-histories/${id}`, data);

  return response.data;
}

export async function getLastDailyWorkRequest(
  userId: number,
  params?: {
    date?: string;
  },
): Promise<DailyWork> {
  const response = await api.get<DailyWork>(`/work-histories/${userId}`, {
    params,
  });

  return response.data;
}

export async function getDailyWorksRequest(params?: {
  dateBegin: string;
  dateEnd: string;
  user?: number;
  role?: UserRole;
}): Promise<DailyWorkResponse[]> {
  const response = await api.get<DailyWorkResponse[]>(`/work-histories`, {
    params,
  });

  return response.data;
}

export async function generateDailyWorkReceiptRequest(
  query: GenerateDailyWorkReceiptParams,
): Promise<AxiosResponse<Blob>> {
  const response = await api.get<Blob>(`/work-histories/receipt`, {
    params: {
      ...query,
      users: query.users.join(','),
    },
    responseType: 'blob',
  });

  return response;
}

export async function generateUserMirrorRequest(
  query: GenerateUserMirrorRequestParams,
): Promise<AxiosResponse<Blob>> {
  const response = await api.get<Blob>(`/work-histories/mirror/create`, {
    params: {
      employees: query.employees,
      date: query.date,
      epiEmployees: query.epiEmployees,
    },
    responseType: 'blob',
  });

  return response;
}

export async function getDailyWorkReceiptUserListRequest(
  query: Omit<GenerateDailyWorkReceiptParams, 'users'>,
): Promise<ReceiptUserListItem[]> {
  const response = await api.get<ReceiptUserListItem[]>(
    `/work-histories/receipt-list`,
    {
      params: query,
    },
  );

  return response.data;
}

export async function editDailyWorkRequest(
  id: number,
  data: EditDailyWorkData,
): Promise<AxiosResponse<DailyWork>> {
  const response = await api.put<DailyWork>(
    `/work-histories/change-time/${id}`,
    data,
  );

  return response;
}

export async function setDailyWorkBankHourRequest(
  data: SerDailyWorkBankHourData,
): Promise<void> {
  const response = await api.put<void>(`/work-histories/bank-hour`, data);

  return response.data;
}

export async function addDailyWorkManuallyRequest(
  data: AddDailyWorkManuallyRequetData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`/work-histories/manually`, data);

  return response;
}

export async function addDailyWorkJustificationRequest(
  data: AddDailyWorkJustificationRequetData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`/work-histories/justification`, data);

  return response;
}

export async function editDailyWorkJustificationRequest(
  id: number,
  data: EditDailyWorkJustificationRequetData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(
    `/work-histories/justification/edit/${id}`,
    data,
  );

  return response;
}

export async function getLateWorkRequest(
  query: GetLateWorkRequestQuery,
): Promise<AxiosResponse<PaginatedResponse<GetLateWorkResponse>>> {
  const response = await api.get<PaginatedResponse<GetLateWorkResponse>>(
    `/work-histories/late`,
    { params: query },
  );

  return response;
}

export async function getFaultWorkRequest(
  query: GetLateWorkRequestQuery,
): Promise<AxiosResponse<PaginatedResponse<GetLateWorkResponse>>> {
  const response = await api.get<PaginatedResponse<GetLateWorkResponse>>(
    `/work-histories/fault`,
    { params: query },
  );

  return response;
}

export async function editAppointmentsRequest(
  id: number,
  data: EditAppointmentsRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(
    `/work-histories/appointments/${id}`,
    data,
  );

  return response;
}

export async function faultToJustificationRequest(
  id: number,
  data: FaultToJustificationRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`/work-histories/fault/${id}`, data);

  return response;
}

export async function getOverIntervalWorkRequest(
  query: GetLateWorkRequestQuery,
): Promise<AxiosResponse<PaginatedResponse<GetLateWorkResponse>>> {
  const response = await api.get<PaginatedResponse<GetLateWorkResponse>>(
    `/work-histories/over-interval`,
    { params: query },
  );

  return response;
}

export async function addDailyWorkPartialJustificationRequest(
  id: number,
  data: AddDailyWorkPartialJustificationRequetData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(
    `/work-histories/justification/partial/${id}`,
    data,
  );

  return response;
}

export async function removeJustificationRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  const response = await api.delete<void>(
    `/work-histories/justification/${id}`,
  );

  return response;
}

export async function changeScheduled(id: number): Promise<DailyWork> {
  const response = await api.put<DailyWork>(`/work-histories/scheduled/${id}`);

  return response.data;
}

export async function getDailyWorkFiles(
  id: number,
): Promise<AxiosResponse<File[]>> {
  const response = await api.get<File[]>(`/work-histories/${id}/files`);

  return response;
}

export function getLunchAlertsRequest() {
  return api.get<GetLunchAlertsResponse>('/work-histories/lunch-alerts');
}

export type RecalculatedTimes = {
  workTime: number;
  lounchTime: number;
  accumulatorTime: number;
  totalTime: number;
  nocturneTime: number;
};

export type RecalculateDailyWorksResult = {
  dryRun: boolean;
  scanned: number;
  changed: {
    id: number;
    user: number;
    username: string;
    date: string;
    before: RecalculatedTimes;
    after: RecalculatedTimes;
    bankDeltaSeconds: number;
  }[];
  skipped: { id: number; reason: 'paid' | 'no-scale' }[];
  totalBankDeltaSeconds: number;
};

/** dryRun (default) only simulates; pass dryRun: false to apply. */
export async function recalculateDailyWorksRequest(data: {
  ids?: number[];
  users?: number[];
  interval?: string[];
  dryRun?: boolean;
}): Promise<RecalculateDailyWorksResult> {
  const response = await api.post<RecalculateDailyWorksResult>(
    '/work-histories/recalculate',
    data,
  );

  return response.data;
}
